// src/auth/AuthContext.jsx
import { createContext, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import api, { API_BASE_URL } from "../api/client";
import { getAuthClaims, getToken, logout as clearToken } from "./auth";

const AuthContext = createContext(null);

function getServerOrigin() {
  return String(API_BASE_URL || "").replace(/\/api\/?$/i, "");
}

function buildAvatarUrl(value) {
  const source = String(value || "").trim();
  if (!source) return null;
  if (/^https?:\/\//i.test(source)) return source;

  const origin = getServerOrigin();
  return `${origin}/${source.replace(/^\/?storage/i, "storage")}`;
}

function buildUser(profile) {
  const claims = getAuthClaims();
  const firstName = profile?.firstName ?? "";
  const lastName = profile?.lastName ?? "";

  return {
    id: profile?.id ?? claims?.userId ?? null,
    firstName,
    lastName,
    name:
      `${firstName} ${lastName}`.trim() ||
      profile?.name ||
      profile?.email ||
      "LawAfrica user",
    email: profile?.email ?? claims?.payload?.email ?? null,
    role: profile?.role ?? claims?.role ?? null,
    userType: profile?.userType ?? claims?.userType ?? null,
    avatarUrl: buildAvatarUrl(profile?.profileImageUrl),
    institutionId: profile?.institutionId ?? claims?.institutionId ?? null,
    institutionName: profile?.institutionName ?? null,
    isGlobalAdmin: claims?.isGlobalAdmin === true,
    isInstitutionAdmin:
      claims?.isInstitutionAdmin === true ||
      String(claims?.role || "").toLowerCase() === "institutionadmin" ||
      String(claims?.institutionRole || "").toLowerCase() === "institutionadmin",
    institutionRole: claims?.institutionRole ?? null,
    claims: claims?.payload ?? null,
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const inFlightRef = useRef(null);

  const refreshUser = async () => {
    if (inFlightRef.current) return inFlightRef.current;

    const run = (async () => {
      try {
        const response = await api.get("/Profile/me", {
          __skipThrottle: true,
          __skipDedupe: true,
        });

        setUser(buildUser(response.data));
        return true;
      } catch (error) {
        if (axios.isCancel(error) || error?.code === "ERR_CANCELED") return false;

        if (error?.response?.status === 401) {
          clearToken();
          setUser(null);
          return false;
        }

        // A temporary profile failure should not prevent the shell from mounting.
        // When a valid JWT exists, retain role/institution context from claims.
        const token = getToken();
        if (token) setUser(buildUser(null));

        console.error("Profile load failed", error);
        return false;
      } finally {
        inFlightRef.current = null;
      }
    })();

    inFlightRef.current = run;
    return run;
  };

  useEffect(() => {
    let active = true;

    const bootstrap = async () => {
      const token = getToken();

      if (!token) {
        if (active) {
          setUser(null);
          setLoading(false);
        }
        return;
      }

      try {
        await refreshUser();
      } finally {
        if (active) setLoading(false);
      }
    };

    bootstrap();

    return () => {
      active = false;
    };
    // refreshUser intentionally stays stable for the life of this provider.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    clearToken();
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        logout,
        refreshUser,
        isGlobalAdmin: user?.isGlobalAdmin === true,
        isInstitutionAdmin: user?.isInstitutionAdmin === true,
        institutionId: user?.institutionId ?? null,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
